/**
 * Jobs Slice
 * Job management state with async thunks and polling
 *
 * UPDATED:
 * - Added createConfigJob thunk for config-based jobs (multi-camera)
 */

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  JobsState,
  Job,
  JobStatsResponse,
  JobType,
  JobStatus,
  MultiCameraJobConfig,
} from "../../types";
import * as jobsAPI from "../../api/jobs";

// Initial state
const initialState: JobsState = {
  jobs: [],
  currentJob: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    page: 1,
    page_size: 20,
    total: 0,
  },
  filters: {
    status: null,
    type: null,
  },
};

/**
 * Create new file-based job
 */
export const createJob = createAsyncThunk<
  Job,
  {
    jobType: JobType;
    file: File;
    onProgress?: (progress: number) => void;
    isLiveDemo?: boolean;
    sessionData?: any;
  },
  { rejectValue: string }
>(
  "jobs/create",
  async (
    { jobType, file, onProgress, isLiveDemo, sessionData },
    { rejectWithValue }
  ) => {
    try {
      const response = await jobsAPI.createJob(
        jobType,
        file,
        onProgress,
        isLiveDemo,
        sessionData
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to create job");
    }
  }
);

/**
 * Create new config-based job (e.g., multi-camera system)
 */
export const createConfigJob = createAsyncThunk<
  Job,
  { jobType: JobType; config: MultiCameraJobConfig },
  { rejectValue: string }
>("jobs/createConfig", async ({ jobType, config }, { rejectWithValue }) => {
  try {
    const response = await jobsAPI.createConfigJob(jobType, config);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to create job");
  }
});

/**
 * Fetch jobs with pagination and filters
 */
export const fetchJobs = createAsyncThunk<
  { jobs: Job[]; total: number; page: number; page_size: number },
  { page?: number; page_size?: number; status?: JobStatus; type?: JobType },
  { rejectValue: string }
>(
  "jobs/fetchJobs",
  async ({ page = 1, page_size = 20, status, type }, { rejectWithValue }) => {
    try {
      const response = await jobsAPI.getJobs({
        page,
        page_size,
        status_filter: status,
        type_filter: type,
      });
      return {
        jobs: response.jobs,
        total: response.total,
        page: response.page,
        page_size: response.page_size,
      };
    } catch (error: any) {
      return rejectWithValue(error.message || "Failed to fetch jobs");
    }
  }
);

/**
 * Fetch single job by ID
 */
export const fetchJobById = createAsyncThunk<
  Job,
  string,
  { rejectValue: string }
>("jobs/fetchJobById", async (jobId, { rejectWithValue }) => {
  try {
    const response = await jobsAPI.getJobById(jobId);
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch job");
  }
});

/**
 * Delete job
 */
export const deleteJob = createAsyncThunk<
  string,
  string,
  { rejectValue: string }
>("jobs/delete", async (jobId, { rejectWithValue }) => {
  try {
    await jobsAPI.deleteJob(jobId);
    return jobId;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to delete job");
  }
});

/**
 * Fetch job statistics
 */
export const fetchJobStats = createAsyncThunk<
  JobStatsResponse,
  void,
  { rejectValue: string }
>("jobs/fetchStats", async (_, { rejectWithValue }) => {
  try {
    const response = await jobsAPI.getJobStats();
    return response;
  } catch (error: any) {
    return rejectWithValue(error.message || "Failed to fetch stats");
  }
});

/**
 * Poll job status (for real-time updates)
 */
export const pollJob = createAsyncThunk<Job, string, { rejectValue: string }>(
  "jobs/poll",
  async (jobId, { rejectWithValue, dispatch }) => {
    try {
      const job = await jobsAPI.pollJobStatus(jobId, 3000, (updatedJob) => {
        // Update job in real-time
        dispatch(updateJobInList(updatedJob));
      });
      return job;
    } catch (error: any) {
      return rejectWithValue(error.message || "Polling failed");
    }
  }
);

/**
 * Jobs slice
 */
const jobsSlice = createSlice({
  name: "jobs",
  initialState,
  reducers: {
    /**
     * Set current job
     */
    setCurrentJob: (state, action: PayloadAction<Job | null>) => {
      state.currentJob = action.payload;
    },

    /**
     * Update job in list (for polling updates)
     */
    updateJobInList: (state, action: PayloadAction<Job>) => {
      const index = state.jobs.findIndex((job) => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = action.payload;
      }

      // Also update currentJob if it matches
      if (state.currentJob?.id === action.payload.id) {
        state.currentJob = action.payload;
      }
    },

    /**
     * Set filters
     */
    setFilters: (
      state,
      action: PayloadAction<{
        status?: JobStatus | null;
        type?: JobType | null;
      }>
    ) => {
      state.filters = {
        status: action.payload.status ?? state.filters.status,
        type: action.payload.type ?? state.filters.type,
      };
    },

    /**
     * Clear filters
     */
    clearFilters: (state) => {
      state.filters = {
        status: null,
        type: null,
      };
    },

    /**
     * Clear error
     */
    clearError: (state) => {
      state.error = null;
    },

    /**
     * Add job to list (optimistic update after creation)
     */
    addJobToList: (state, action: PayloadAction<Job>) => {
      state.jobs.unshift(action.payload);
      state.pagination.total += 1;
    },

    /**
     * Remove job from list (optimistic update after deletion)
     */
    removeJobFromList: (state, action: PayloadAction<string>) => {
      state.jobs = state.jobs.filter((job) => job.id !== action.payload);
      state.pagination.total -= 1;
    },
  },
  extraReducers: (builder) => {
    // Create file-based job
    builder.addCase(createJob.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createJob.fulfilled, (state, action) => {
      state.loading = false;
      state.jobs.unshift(action.payload);
      state.pagination.total += 1;
    });
    builder.addCase(createJob.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to create job";
    });

    // Create config-based job
    builder.addCase(createConfigJob.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(createConfigJob.fulfilled, (state, action) => {
      state.loading = false;
      state.jobs.unshift(action.payload);
      state.pagination.total += 1;
    });
    builder.addCase(createConfigJob.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to create config job";
    });

    // Fetch jobs
    builder.addCase(fetchJobs.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobs.fulfilled, (state, action) => {
      state.loading = false;
      state.jobs = action.payload.jobs;
      state.pagination = {
        page: action.payload.page,
        page_size: action.payload.page_size,
        total: action.payload.total,
      };
    });
    builder.addCase(fetchJobs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch jobs";
    });

    // Fetch job by ID
    builder.addCase(fetchJobById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchJobById.fulfilled, (state, action) => {
      state.loading = false;
      state.currentJob = action.payload;

      // Update in list if exists
      const index = state.jobs.findIndex((job) => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = action.payload;
      }
    });
    builder.addCase(fetchJobById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch job";
    });

    // Delete job
    builder.addCase(deleteJob.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(deleteJob.fulfilled, (state, action) => {
      state.loading = false;
      state.jobs = state.jobs.filter((job) => job.id !== action.payload);
      state.pagination.total -= 1;

      // Clear currentJob if it was deleted
      if (state.currentJob?.id === action.payload) {
        state.currentJob = null;
      }
    });
    builder.addCase(deleteJob.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to delete job";
    });

    // Fetch stats
    builder.addCase(fetchJobStats.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(fetchJobStats.fulfilled, (state, action) => {
      state.loading = false;
      state.stats = action.payload;
    });
    builder.addCase(fetchJobStats.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || "Failed to fetch stats";
    });

    // Poll job
    builder.addCase(pollJob.fulfilled, (state, action) => {
      // Update job in list
      const index = state.jobs.findIndex((job) => job.id === action.payload.id);
      if (index !== -1) {
        state.jobs[index] = action.payload;
      }

      // Update currentJob if it matches
      if (state.currentJob?.id === action.payload.id) {
        state.currentJob = action.payload;
      }
    });
  },
});

export const {
  setCurrentJob,
  updateJobInList,
  setFilters,
  clearFilters,
  clearError,
  addJobToList,
  removeJobFromList,
} = jobsSlice.actions;

export default jobsSlice.reducer;
